import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { useCallback, useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';

import type { AgentMetadata, ChatState } from '../useChatStore';
import { useChatStore } from '../useChatStore';

/**
 * Agent-mode chat adaptor.
 *
 * Web-side adaptor for the read-only Agent backend (`services/ai-gateway`). It
 * owns a DEDICATED Socket.IO connection to the gateway (separate from the
 * existing platform-knowledge chat socket, which talks to the ai-engine) and
 * maps the gateway's clean event contract — `message_chunk` / `tool_call_start`
 * / `tool_call_complete` / `message_complete` / `error` (camelCase, no
 * `session_id`; the session is owned server-side per (user, org)) — onto the
 * shared chat store. Because the socket is dedicated to this turn's agent, no
 * client-side `session_id` filtering is needed.
 *
 * Auth: the org-scoped Auth0 access token is sent in the handshake AND with each
 * `send_message` (the gateway re-verifies a refreshed per-turn token), covering
 * the 30-min token lifetime without a reconnect.
 */

// Tool-args shape, reused from the store rather than re-declared.
type ToolArgs = NonNullable<
  NonNullable<AgentMetadata['toolCalls']>[number]['toolArgs']
>;

// Gateway event payloads — mirror services/ai-gateway `ChatServerEvent`.
interface MessageChunkEvent {
  delta: string;
}
interface ToolCallStartEvent {
  toolUseId: string;
  name: string;
  input: unknown;
}
interface ToolCallCompleteEvent {
  toolUseId: string;
  name: string;
  isError: boolean;
  result: string;
}
interface MessageCompleteEvent {
  stopReason: string | null;
}
interface GatewayErrorEvent {
  error: string;
}

const isToolArgs = (value: unknown): value is ToolArgs =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Map the gateway's opaque error codes to user-facing copy. */
const agentErrorMessage = (code: string): string => {
  switch (code) {
    case 'rate_limited':
      return 'You are sending messages too quickly — please wait a moment and try again.';
    case 'message_too_long':
      return 'That message is too long. Please shorten it and try again.';
    case 'session_conflict':
      return 'Your organisation context changed. Please reopen the agent to continue.';
    case 'unauthorized':
      return 'Your session has expired. Please refresh the page and try again.';
    default:
      return 'The agent ran into a problem. Please try again.';
  }
};

export const useAgentChat = () => {
  const { getAccessTokenSilently } = useRisksmartUser();
  const socketRef = useRef<Socket | null>(null);

  /** Connect (or reuse) the dedicated gateway socket, authed with a fresh token. */
  const connect = useCallback(async (): Promise<Socket> => {
    const existing = socketRef.current;
    if (existing?.connected) {
      return existing;
    }
    existing?.disconnect();

    const url = getEnv('REACT_APP_AI_AGENT_GATEWAY_URL', true);
    if (!url) {
      throw new Error('Agent gateway is not configured');
    }
    const token = await getAccessTokenSilently();

    // The gateway is path-routed at /ai-gateway/* on the shared tenant ALB host
    // (single-name cert → no dedicated subdomain), so the engine.io path is
    // prefixed to match. Must stay in lockstep with the gateway's SOCKET_IO_PATH.
    const socket = io(url, {
      path: '/ai-gateway/socket.io/',
      auth: { token },
    });
    socketRef.current = socket;

    await new Promise<void>((resolve, reject) => {
      socket.once('connect', () => resolve());
      socket.once('connect_error', (err: Error) => {
        socket.disconnect();
        socketRef.current = null;
        reject(err);
      });
    });

    return socket;
  }, [getAccessTokenSilently]);

  const sendAgentMessage = useCallback(
    async (content: string): Promise<void> => {
      const store = useChatStore.getState();
      store.addMessage(content, true);
      store.setIsLoading(true);
      // Cold-start affordance: a thinking step shown BENEATH the user's message
      // (not isInitialising, which would hide the message) until the first event
      // — AgentCore cold start can take a few seconds.
      store.setCurrentThinkingStep('Starting your agent…');

      let socket: Socket;
      try {
        socket = await connect();
      } catch {
        const s = useChatStore.getState();
        s.setCurrentThinkingStep(null);
        s.setIsLoading(false);
        s.addMessage(agentErrorMessage('agent_unavailable'), false, true);

        return;
      }

      const token = await getAccessTokenSilently();
      let streamingId: string | null = null;

      await new Promise<void>((resolve) => {
        // Open the assistant message on the FIRST event of ANY kind. The agent
        // commonly emits tool_call_* BEFORE any text token (it queries data,
        // then writes the answer), and the store drops tool metadata when there
        // is no streaming message yet — so opening only on the first chunk would
        // lose the tool-call surface for the common case.
        const ensureStreaming = (s: ChatState): void => {
          if (!streamingId) {
            streamingId = uuidv4();
            s.setCurrentThinkingStep(null);
            s.startStreamingMessage(streamingId);
          }
        };

        const onChunk = (data: MessageChunkEvent) => {
          const s = useChatStore.getState();
          ensureStreaming(s);
          s.appendToStreamingMessage(data.delta);
        };

        const onToolStart = (data: ToolCallStartEvent) => {
          const s = useChatStore.getState();
          ensureStreaming(s);
          s.addToolCallToMetadata(
            data.name,
            new Date().toISOString(),
            isToolArgs(data.input) ? data.input : undefined,
            data.toolUseId
          );
        };

        const onToolComplete = (data: ToolCallCompleteEvent) => {
          const s = useChatStore.getState();
          ensureStreaming(s);
          s.completeToolCallInMetadata({
            toolName: data.name,
            endTime: new Date().toISOString(),
            success: !data.isError,
            result: data.result,
            toolUseId: data.toolUseId,
          });
        };

        const finish = () => {
          const s = useChatStore.getState();
          s.finishStreamingMessage();
          s.setCurrentThinkingStep(null);
          s.setIsLoading(false);
          cleanup();
          resolve();
        };

        const onComplete = (_data: MessageCompleteEvent) => finish();

        const onError = (data: GatewayErrorEvent) => {
          const s = useChatStore.getState();
          if (streamingId) {
            // Mid-stream failure: flag the truncation inline so a half-finished
            // answer isn't mistaken for a complete one.
            s.appendToStreamingMessage(
              `\n\n_${agentErrorMessage(data.error)}_`
            );
          } else {
            s.addMessage(agentErrorMessage(data.error), false, true);
          }
          finish();
        };

        // A mid-turn disconnect (network blip / server restart / panel close)
        // sends no terminal frame, so without this the promise never settles and
        // the input is wedged on a permanent spinner.
        const onDisconnect = () => {
          if (!streamingId) {
            useChatStore
              .getState()
              .addMessage(agentErrorMessage('agent_unavailable'), false, true);
          }
          finish();
        };

        const cleanup = () => {
          socket.off('message_chunk', onChunk);
          socket.off('tool_call_start', onToolStart);
          socket.off('tool_call_complete', onToolComplete);
          socket.off('message_complete', onComplete);
          socket.off('error', onError);
          socket.off('disconnect', onDisconnect);
        };

        socket.on('message_chunk', onChunk);
        socket.on('tool_call_start', onToolStart);
        socket.on('tool_call_complete', onToolComplete);
        socket.on('message_complete', onComplete);
        socket.on('error', onError);
        socket.once('disconnect', onDisconnect);

        socket.emit('send_message', { content, token });
      });
    },
    [connect, getAccessTokenSilently]
  );

  // Tear the dedicated socket down on unmount, and clear any in-flight turn's
  // transient loading state so a reopened panel never inherits a stuck spinner.
  useEffect(
    () => () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
      const s = useChatStore.getState();
      s.setIsLoading(false);
      s.setCurrentThinkingStep(null);
    },
    []
  );

  return { sendAgentMessage, connectAgent: connect };
};
